import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AdminInvitePreviewBody } from './AdminInvitePreviewModal';

// Pre-send confirmation modal for the admin resend-invitation flow. Separate component
// from the invite modal (mirrors the medical-staff split) but shares the same email
// card — here in its `resend` variant (재발송 badge + "previous link invalidated"
// notice). Populated with the target admin's stored values from the edit panel.

const I18N = 'adminManagement.invitePreviewModal';

interface AdminResendPreviewModalProps {
  open: boolean;
  name: string;
  email: string;
  rolesLabel: string;
  sentAt: string;
  submitting: boolean;
  // [← 수정] — close the preview without resending.
  onEdit: () => void;
  // [✉ 재발송] — run the actual resend.
  onConfirm: () => void;
}

export function AdminResendPreviewModal({
  open,
  name,
  email,
  rolesLabel,
  sentAt,
  submitting,
  onEdit,
  onConfirm,
}: AdminResendPreviewModalProps) {
  const { t } = useTranslation('admin');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onEdit]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={onEdit}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal"
        style={{
          maxWidth: 640,
          width: '92vw',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 className="modal__title">{t(`${I18N}.resendTitle`)}</h2>
        </div>
        <div className="modal__body" style={{ overflowY: 'auto' }}>
          <AdminInvitePreviewBody
            name={name}
            email={email}
            rolesLabel={rolesLabel}
            sentAt={sentAt}
            resend
          />
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onEdit}
            disabled={submitting}
          >
            {t(`${I18N}.editButton`)}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onConfirm}
            disabled={submitting}
          >
            {t(`${I18N}.resendButton`)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
